import type { IConfirmDialogProps } from 'components/ConfirmDialog';
import { State } from 'utils/state';

export type DialogStatus = 'idle' | 'opening' | 'open' | 'closing';

export interface IConfirmDialogState extends Omit<IConfirmDialogProps, 'isOpen'> {
  isOpen?: boolean;
  isBeingRendered?: boolean;
  status?: DialogStatus;
}

const defaults: Partial<IConfirmDialogState> = {
  confirmText: 'Confirm',
  cancelText: 'Cancel',
};

export const confirmDialogState = new State<IConfirmDialogState>({
  ...defaults,
  isOpen: false,
  isBeingRendered: false,
  status: 'idle',
  title: '',
  onConfirm: () => {},
  onCancel: () => {},
});

/**
 * Shows a confirmation dialog from anywhere in the app.
 * Only one dialog can be shown at a time (singleton pattern).
 *
 * @example
 * Basic usage:
 * ```tsx
 * import { showConfirmDialog } from 'state/confirm-dialog';
 *
 * const confirmed = await showConfirmDialog({
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?',
 *   confirmText: 'Delete',
 *   cancelText: 'Cancel',
 * });
 *
 * if (confirmed) {
 *   console.log('User confirmed!');
 * } else {
 *   console.log('User cancelled!');
 * }
 * ```
 *
 * @example
 * With custom content (form fields):
 * ```tsx
 * const confirmed = await showConfirmDialog({
 *   title: 'Enter Details',
 *   message: 'Please provide additional information:',
 *   children: (
 *     <div>
 *       <input type="text" placeholder="Name" className="..." />
 *       <textarea placeholder="Notes" className="..." />
 *     </div>
 *   ),
 * });
 * ```
 *
 * @param dialogProps - The dialog configuration (without onConfirm/onCancel callbacks)
 * @returns Promise<boolean> - true if confirmed, false if cancelled
 */
export function showConfirmDialog(
  dialogProps: Omit<IConfirmDialogProps, 'isOpen' | 'onConfirm' | 'onCancel'>
): Promise<boolean> {
  const currentStatus = confirmDialogState.get('status');

  // If a dialog is already active, reject the promise
  if (currentStatus !== 'idle') {
    console.warn('[ConfirmDialog] Dialog already active, rejecting new request');
    return Promise.resolve(false);
  }

  confirmDialogState.setValues({ status: 'opening' });

  return new Promise<boolean>((resolve) => {
    confirmDialogState.setValues({
      ...defaults,
      ...dialogProps,
      onConfirm: () => {
        hideConfirmDialog();
        resolve(true);
      },
      onCancel: () => {
        hideConfirmDialog();
        resolve(false);
      },
      isOpen: true,
      status: 'open',
    });
  });
}

/**
 * Hides the currently displayed confirmation dialog and clears all state.
 */
export function hideConfirmDialog() {
  confirmDialogState.setValues({
    ...defaults,
    isOpen: false,
    isBeingRendered: false,
    status: 'idle',
    title: '',
    message: undefined,
    children: undefined,
    onConfirm: () => {},
    onCancel: () => {},
  });
}

/**
 * Resets the confirmation dialog state completely.
 */
export function resetConfirmDialogState() {
  confirmDialogState.setValues(
    {
      ...defaults,
      isOpen: false,
      isBeingRendered: false,
      status: 'idle',
      title: '',
      onConfirm: () => {},
      onCancel: () => {},
    },
    { reset: true }
  );
}

/**
 * Claims the dialog for rendering. Returns true if successfully claimed.
 * This prevents multiple instances of the dialog from rendering simultaneously.
 */
export function claimConfirmDialogRendering(): boolean {
  const currentState = confirmDialogState.get();
  if (currentState.isOpen && !currentState.isBeingRendered) {
    confirmDialogState.setValues({ isBeingRendered: true });
    return true;
  }
  return false;
}

// Reset the status to idle when the dialog is hidden
confirmDialogState.on('change', (changedKeys, _changedValues, _oldValues, _values, currentState) => {
  if (changedKeys.includes('isOpen') && currentState.isOpen === false) {
    confirmDialogState.setValues({ isBeingRendered: false, status: 'idle' });
  }
});

export default confirmDialogState;
